import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { SubscriptionType } from '@/types/shared';
import { refreshDefaultHeaders } from './auth-headers';

const CLAIM_RETRY_DELAYS_MS = [500, 1_000, 2_000, 4_000, 8_000, 16_000];
const SUBSCRIPTION_TYPES = Object.values(SubscriptionType);

interface AuthTokenProvider {
  getIdToken(forceRefresh?: boolean): Promise<string | null | undefined>;
}

interface SubscriptionTypeClaimResponse {
  subscriptionType: SubscriptionType;
}

const wait = (delayMs: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, delayMs));

const hasExpectedSubscription = (actual: SubscriptionType, expected: SubscriptionType): boolean =>
  SUBSCRIPTION_TYPES.indexOf(actual) >= SUBSCRIPTION_TYPES.indexOf(expected);

/**
 * Waits for Core to observe a Stripe upgrade, updates the Firebase custom claim, and then
 * force-refreshes the browser's ID token so subsequent requests carry the new claim.
 */
export const refreshSubscriptionClaimAfterUpgrade = async (
  authTokenProvider: AuthTokenProvider,
  expectedSubscription: SubscriptionType,
): Promise<boolean> => {
  for (let attempt = 0; attempt <= CLAIM_RETRY_DELAYS_MS.length; attempt += 1) {
    const response = await safeAxios.post<SubscriptionTypeClaimResponse>(
      API_ROUTES.users.subscriptionTypeClaimRefresh(),
    );
    if (hasExpectedSubscription(response.data.subscriptionType, expectedSubscription)) {
      await refreshDefaultHeaders(authTokenProvider);
      return true;
    }

    const retryDelay = CLAIM_RETRY_DELAYS_MS[attempt];
    if (retryDelay === undefined) {
      return false;
    }
    await wait(retryDelay);
  }

  return false;
};
